/**
 * Place state — the one answer to "what do my taps do right now?"
 *
 * WHY THIS EXISTS. Testers said the customer pages "are not synced". They were
 * right, and this is the mechanism: every customer surface derived its own idea
 * of the guest's situation from whatever props happened to be in scope. The menu
 * knew about the table session, the pay page knew about the order, the reserve
 * page knew about neither, and none of them agreed. A guest who scanned a table
 * QR, ordered, then tapped "Reserve" landed on a page that behaved as if they had
 * just arrived from Google.
 *
 * So the guest's situation is resolved ONCE, here, as a pure function, and every
 * surface renders the same sentence from it. The sentence is the product's
 * continuity object: it is the only element that is present, in the same place,
 * saying a true thing, on every screen from arrival to receipt.
 *
 * PURE AND ISOMORPHIC. No React, no fetch, no Date.now() except through the
 * injected `now`. That is what makes it testable and what stops the two-copies
 * drift this file exists to end.
 *
 * WHO RENDERS IT NOW. The standalone 34px ribbon this was written for has gone:
 * its most common content was the two words "Open now", a permanent band of
 * chrome to say nothing was happening. The state is now split by what a guest
 * can DO with it — the header states where they are, the action bar states what
 * happens next — so this resolver feeds both.
 *
 * @see components/place/PlaceHeader.tsx — the "where you are" half
 * @see lib/customer/place-action.ts — the "what happens next" half
 */

import {
  WEEKDAYS,
  findNextOpenInstant,
  isBranchOpenNow,
  normalizeDayShifts,
  readBranchOperatingHours,
} from '@/lib/branch-operating-hours';
import { isOrderOver, type OrderStatus } from '@/lib/order-lifecycle';

// ── The stance ──────────────────────────────────────────────────────────────

/**
 * What the guest is *doing*, not what page they are on. Ordered by precedence:
 * a live order outranks a cart, which outranks a seat, which outranks browsing.
 * A guest with food coming cares about the food, not about the menu they are
 * standing on.
 */
export type PlaceStance =
  /** Branch is shut. Nothing the guest taps can reach the kitchen. */
  | 'closed'
  /** No seat, no cart, no order. Reading the menu. */
  | 'browsing'
  /** Seated via a table QR. Taps reach the kitchen. */
  | 'seated'
  /** At a counter/pickup QR. Taps reach the till. */
  | 'counter'
  /** Items chosen, nothing submitted yet. */
  | 'building'
  /** An order is in flight with the kitchen. */
  | 'live_order'
  /** Order done, money outstanding. */
  | 'awaiting_payment';

export type PlaceTone =
  /** Chrome colours only. The overwhelming default. */
  | 'neutral'
  /** Something is happening for the guest right now. Earns the accent. */
  | 'live'
  /** The guest must act or something stalls. */
  | 'attention';

export type PlaceState = {
  stance: PlaceStance;
  /**
   * The lead clause, in plain words, from the guest's side of the screen.
   * "Table 7", "Order #A24", "Browsing". Never a system noun.
   */
  label: string;
  /**
   * What that means for their taps: "orders go to the kitchen".
   * Omitted when there is nothing true and useful to add — an empty detail is
   * better than a filler one.
   */
  detail?: string;
  /**
   * Where tapping the ribbon goes, relative to the place root (e.g. `/cart`).
   * Undefined makes the ribbon a statement rather than a control, and it must
   * then not look tappable.
   */
  href?: string;
  /**
   * Semantic action the ribbon can dispatch instead of navigating. The shell
   * maps this to a registered callback (e.g. opening the cart sheet) so the
   * guest never leaves the menu to see their order.
   */
  action?: string;
  tone: PlaceTone;
};

// ── Inputs ──────────────────────────────────────────────────────────────────

export type PlaceStateInput = {
  branch?: {
    operating_hours?: unknown;
    timezone?: string | null;
  } | null;
  /** Resolved table/counter session, if the guest scanned a seat or till QR. */
  session?: {
    table?: { label?: string | null; is_counter_pickup?: boolean | null } | null;
    session?: { status?: string | null } | null;
  } | null;
  /** The guest's most recent order that has not finished. */
  order?: {
    /** Short human code the guest can read aloud to staff. Never the UUID. */
    code?: string | null;
    status?: OrderStatus | string | null;
    /** True once the money is settled. */
    paid?: boolean | null;
    /**
     * Full-page track URL for this order, token included. Used as the fallback
     * destination when the orders SHEET is unavailable — see the note on
     * `action` below.
     */
    trackHref?: string | null;
  } | null;
  /** Items in the cart that have not been submitted. */
  cartCount?: number;
  /** Formatted cart total, already money-formatted by the caller. */
  cartTotal?: string | null;
  /**
   * The fulfilment mode the guest picked when they are not in a scanned
   * session — pickup, delivery or a scheduled pre-order. A seated guest's mode
   * is decided by the QR they scanned, so this is ignored for them.
   *
   * The mode and the ribbon say the same thing, so they are one element: the
   * old shell had a mode switcher in a collapsed panel AND a session chip
   * underneath it, which could disagree.
   */
  mode?: 'menu' | 'dine_in' | 'pickup' | 'delivery' | 'scheduled' | null;
  /** True when the branch offers more than one ordering mode and the guest can
   *  change their current choice. When set, the status line becomes interactive
   *  so the guest can switch modes without adding items first. */
  modeChangeAvailable?: boolean;
  /** Injected for testability. */
  now?: Date;
};

// ── Words ───────────────────────────────────────────────────────────────────

/**
 * What each kitchen state means to the person waiting, not to the kitchen.
 * "preparing" is an operations word; "being cooked" is what the guest is
 * actually wondering about.
 */
const ORDER_WORDS: Record<string, { detail: string; tone: PlaceTone }> = {
  placed: { detail: 'sent to the kitchen', tone: 'live' },
  accepted: { detail: 'accepted', tone: 'live' },
  preparing: { detail: 'being cooked', tone: 'live' },
  ready: { detail: 'ready for you', tone: 'attention' },
  delivered: { detail: 'on its way over', tone: 'live' },
  served: { detail: 'served', tone: 'live' },
  delivery_exception: { detail: 'held up — staff are on it', tone: 'attention' },
};

// ── Resolution ──────────────────────────────────────────────────────────────

/**
 * Resolve the guest's situation. Precedence is deliberate and is the whole
 * point: the ribbon says the single most important true thing, so the guest
 * never has to work out which of several banners applies to them.
 */
export function resolvePlaceState(input: PlaceStateInput): PlaceState {
  const now = input.now ?? new Date();

  // 1. Money outstanding beats everything. It is the only state where the guest
  //    is blocking, and the only one where the merchant is exposed.
  const order = input.order;
  const orderStatus = typeof order?.status === 'string' ? order.status : null;
  const orderLive = Boolean(orderStatus) && !isOrderOver(orderStatus);

  if (order && orderLive && order.paid === false && orderStatus === 'ready') {
    return {
      stance: 'awaiting_payment',
      label: orderCode(order.code),
      detail: 'ready to pay',
      // NOT `href: '/pay'` — that route does not exist under /m/<slug>.
      //
      // The sheet is still preferred: it keeps the guest on the menu. But the
      // sheet is registered by the ordering island, which is not always mounted,
      // and this is the shell's primary full-width button — so when the island
      // is absent this used to render a dead control announcing the guest's own
      // order. `trackHref` is the real page (`/m/<slug>/orders/<id>?token=…`),
      // carried so the consumer can be a link when it cannot be a button.
      //
      // (An earlier comment here claimed `/order` "does not exist and never
      // did". `/m/[business]/orders/[orderId]` does exist; that claim was stale
      // and is why this stance only ever offered a sheet.)
      action: 'openOrders',
      href: order.trackHref ?? undefined,
      tone: 'attention',
    };
  }

  // 2. An order in flight. The guest is waiting on the kitchen, so the ribbon
  //    becomes the tracker and stops talking about the menu.
  if (order && orderLive && orderStatus) {
    const words = ORDER_WORDS[orderStatus];
    if (words) {
      return {
        stance: 'live_order',
        label: orderCode(order.code),
        detail: words.detail,
        action: 'openOrders',
        href: order.trackHref ?? undefined,
        tone: words.tone,
      };
    }
  }

  // 3. A cart with something in it. The guest has committed to choices and the
  //    ribbon becomes the way back to them from anywhere in the place.
  const cartCount = input.cartCount ?? 0;
  if (cartCount > 0) {
    return {
      stance: 'building',
      label: cartCount === 1 ? '1 item chosen' : `${cartCount} items chosen`,
      detail: input.cartTotal || undefined,
      action: 'openCart',
      tone: 'live',
    };
  }

  // 4. Seated or at the counter. No order yet, but the guest's taps have a
  //    destination and they need to know which one.
  const session = input.session;
  const sessionActive = session?.session?.status === 'active';
  if (session && sessionActive) {
    if (session.table?.is_counter_pickup) {
      return {
        stance: 'counter',
        label: 'At the counter',
        detail: 'order here, collect at the till',
        tone: 'neutral',
      };
    }
    const tableLabel = session.table?.label?.trim();
    return {
      stance: 'seated',
      label: tableLabel ? `Table ${stripTableWord(tableLabel)}` : 'At your table',
      detail: 'orders go to the kitchen',
      tone: 'neutral',
    };
  }

  // 5. No seat, but the guest chose how they want the food. Say that, because
  //    it is what their next tap will do. When the branch offers more than one
  //    way to get food, the status line becomes the mode switcher — the guest
  //    can change their mind without adding items first.
  const modeWords = input.mode ? MODE_WORDS[input.mode] : null;
  if (modeWords) {
    return {
      stance: 'browsing',
      label: modeWords.label,
      detail: modeWords.detail,
      action: input.modeChangeAvailable ? 'changeMode' : undefined,
      tone: 'neutral',
    };
  }

  // 6. Nothing in play — so the only useful thing to say is whether the guest
  //    can actually get food, and if not, when they can.
  return resolveOpenState(input.branch, now);
}

/**
 * `menu` is deliberately absent: "Browse" is not a fulfilment choice, it is the
 * absence of one, so it falls through to the open/closed sentence, which is
 * strictly more useful to a guest who has not decided anything yet.
 *
 * Exported so the menu page can render the same words in the header's mode
 * switcher — one copy of the guest-facing mode vocabulary.
 */
export const MODE_WORDS: Record<string, { label: string; detail: string }> = {
  dine_in: { label: 'Dining in', detail: 'orders go to the kitchen' },
  pickup: { label: 'Pickup', detail: 'order ahead, collect in person' },
  delivery: { label: 'Delivery', detail: 'to the address you give at checkout' },
  scheduled: { label: 'Ordering ahead', detail: 'pick a time at checkout' },
};

/**
 * Open/closed, phrased as a consequence rather than a status. "Closed" alone
 * leaves the guest with nowhere to go; "opens tomorrow 07:00" is an answer.
 */
function resolveOpenState(
  branch: PlaceStateInput['branch'],
  now: Date,
): PlaceState {
  if (!branch) {
    return { stance: 'browsing', label: 'Browsing', tone: 'neutral' };
  }

  const timezone = branch.timezone || 'UTC';
  let hours;
  try {
    hours = readBranchOperatingHours(branch.operating_hours);
  } catch {
    // Unreadable hours must not claim the place is shut — a guest turned away
    // by a parsing bug is worse than a guest who walks in and asks.
    return { stance: 'browsing', label: 'Browsing', tone: 'neutral' };
  }

  // readBranchOperatingHours cannot fail: unparseable input and "the merchant
  // never filled this in" both come back as every-day-closed. Silence is not a
  // declaration that the place is shut, so only claim "Closed" when the
  // merchant actually opened on some day of the week. Otherwise a business that
  // simply skipped the hours step would tell every guest, forever, that it is
  // closed — with no reopening time to soften it.
  if (!hasDeclaredHours(hours)) {
    return { stance: 'browsing', label: 'Browsing', tone: 'neutral' };
  }

  let open: boolean;
  try {
    open = isBranchOpenNow(hours, timezone, now);
  } catch {
    return { stance: 'browsing', label: 'Browsing', tone: 'neutral' };
  }

  if (open) {
    return { stance: 'browsing', label: 'Open now', tone: 'neutral' };
  }

  let nextOpen: Date | null = null;
  try {
    nextOpen = findNextOpenInstant(hours, timezone, now);
  } catch {
    nextOpen = null;
  }

  return {
    stance: 'closed',
    label: 'Closed',
    detail: nextOpen ? `opens ${formatNextOpen(nextOpen, now, timezone)}` : undefined,
    tone: 'neutral',
  };
}

/** True when the merchant opened on at least one day of the week. */
function hasDeclaredHours(hours: ReturnType<typeof readBranchOperatingHours>): boolean {
  return WEEKDAYS.some((day) => normalizeDayShifts(hours[day]).length > 0);
}

// ── Formatting ──────────────────────────────────────────────────────────────

/**
 * "opens 07:00" / "opens tomorrow 07:00" / "opens Mon 07:00" — relative where a
 * person would say it relatively, absolute once that stops being clearer.
 */
function formatNextOpen(next: Date, now: Date, timezone: string): string {
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(next);

  const dayKey = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(d);

  const today = dayKey(now);
  const target = dayKey(next);
  if (today === target) return time;

  const tomorrow = dayKey(new Date(now.getTime() + 86_400_000));
  if (tomorrow === target) return `tomorrow ${time}`;

  const weekday = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    timeZone: timezone,
  }).format(next);
  return `${weekday} ${time}`;
}

/**
 * Guests read their order number aloud to staff, so it is shown exactly as
 * printed on the ticket. Falls back to a neutral noun rather than exposing a
 * UUID the guest cannot say.
 */
function orderCode(code: string | null | undefined): string {
  const trimmed = code?.trim();
  if (!trimmed) return 'Your order';
  return trimmed.startsWith('#') ? trimmed : `Order ${trimmed}`;
}

/**
 * Table labels are stored however the merchant typed them — "7", "Table 7",
 * "T7". The ribbon supplies the word "Table" itself, so strip a duplicate
 * rather than printing "Table Table 7".
 */
function stripTableWord(label: string): string {
  return label.replace(/^\s*(table|tbl|t)\s*[.:-]?\s*/i, '').trim() || label;
}
